import React, { useCallback, useEffect, useMemo, useState } from "react";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { MapPin, Stethoscope, Signature, ShieldAlert, Shield, FileText, Download } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import MapPicker from "@/components/clinic-growth/MapPicker";

export default function ClinicGrowth() {
  return (
    <AppShell
      pageTitle="ClinicGrowth & CareOS"
      pageDescription="Hyper-local campaigns, AI-triaged telehealth and RMP-compliant e-prescriptions — all built for the Indian healthcare ecosystem."
    >
      {/* Brand hero */}
      <div className="mb-6 rounded-xl border border-sky-200 bg-gradient-to-br from-sky-50 via-white to-sky-50/40 p-4 md:p-5 flex items-center gap-4 md:gap-6" data-testid="cg-hero">
        <img
          src="/assets/clinic-growth-logo.webp"
          alt="ClinicGrowth & CareOS"
          className="h-16 w-16 md:h-20 md:w-20 shrink-0 rounded-xl shadow-sm"
        />
        <div className="min-w-0">
          <div className="text-[11px] uppercase tracking-widest text-sky-800">ClinicGrowth · CareOS</div>
          <div className="font-serif text-xl md:text-2xl text-primary leading-tight">
            Grow your clinic. Keep your boundaries.
          </div>
          <div className="mt-1 text-xs text-muted-foreground max-w-xl">
            Hyper-local Meta campaigns with strict radius locks, AI symptom triage
            with Telemedicine Guideline compliance, and QR-verifiable e-prescriptions
            dispatched over WhatsApp.
          </div>
        </div>
      </div>

      <Tabs defaultValue="campaigns">
        <TabsList className="flex-wrap h-auto gap-1 bg-muted/30 p-1" data-testid="cg-tabs">
          <TabsTrigger value="campaigns" data-testid="cg-tab-campaigns"><MapPin size={13} className="mr-1" /> Hyper-Local Campaigns</TabsTrigger>
          <TabsTrigger value="triage" data-testid="cg-tab-triage"><Stethoscope size={13} className="mr-1" /> AI Triage Queue</TabsTrigger>
          <TabsTrigger value="signoff" data-testid="cg-tab-signoff"><Signature size={13} className="mr-1" /> RMP Sign-off</TabsTrigger>
        </TabsList>
        <div className="mt-6">
          <TabsContent value="campaigns"><Campaigns /></TabsContent>
          <TabsContent value="triage"><Triage /></TabsContent>
          <TabsContent value="signoff"><Signoff /></TabsContent>
        </div>
      </Tabs>
    </AppShell>
  );
}

// ---------- Hyper-Local Campaigns -----------------------------------------
function Campaigns() {
  const [list, setList] = useState([]);
  const [form, setForm] = useState({ campaign_name: "", target_type: "urban", radius_km: 3,
                                      latitude: 19.0760, longitude: 72.8777, daily_budget_inr: 500 });
  const [nearby, setNearby] = useState([]);
  const [nearbyLoading, setNearbyLoading] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/clinic-growth/campaigns"); setList(data.campaigns); }
    catch (e) { toast.error(formatApiError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Fetch competing campaigns inside the ~25 km probe area whenever the pin moves.
  // Debounced so dragging doesn't hammer the server.
  const probeLat = Number(form.latitude);
  const probeLng = Number(form.longitude);
  useEffect(() => {
    if (!Number.isFinite(probeLat) || !Number.isFinite(probeLng)) return;
    let cancelled = false;
    setNearbyLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/clinic-growth/campaigns/nearby", {
          params: { lat: probeLat, lng: probeLng, max_km: 25 },
        });
        if (!cancelled) setNearby(data.campaigns || []);
      } catch (_) { if (!cancelled) setNearby([]); }
      finally { if (!cancelled) setNearbyLoading(false); }
    }, 450);
    return () => { cancelled = true; clearTimeout(t); };
  }, [probeLat, probeLng]);

  const minR = form.target_type === "urban" ? 1 : 10;
  const maxR = form.target_type === "urban" ? 5 : 20;
  const setType = (t) => setForm((p) => ({ ...p, target_type: t, radius_km: t === "urban" ? 3 : 15 }));

  const overlapCount = useMemo(
    () => nearby.filter((c) => c.overlaps_boundary && !c.is_mine).length,
    [nearby],
  );

  const submit = async () => {
    try {
      await api.post("/clinic-growth/campaigns/create", {
        ...form,
        radius_km: Number(form.radius_km),
        latitude: Number(form.latitude), longitude: Number(form.longitude),
        daily_budget_inr: Number(form.daily_budget_inr) || null,
      });
      toast.success("Campaign deployed with strict spatial locks");
      setForm((p) => ({ ...p, campaign_name: "" }));
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6">
      <div className="rounded-lg border border-border bg-white p-5">
        <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Hyper-Local Boundary Configurator</div>
        <div className="mt-1 font-serif text-xl text-primary">Drop a pin — your boundary locks instantly</div>
        <div className="mt-4 flex gap-2 flex-wrap">
          <Button type="button" variant={form.target_type === "urban" ? "default" : "outline"} onClick={() => setType("urban")} data-testid="cg-type-urban">Urban (1–5 km)</Button>
          <Button type="button" variant={form.target_type === "rural" ? "default" : "outline"} onClick={() => setType("rural")} data-testid="cg-type-rural">Rural (10–20 km)</Button>
        </div>

        <div className="mt-4">
          <Label className="text-xs">Pick clinic location (click or drag the pin)</Label>
          <div className="mt-2">
            <MapPicker
              lat={probeLat}
              lng={probeLng}
              radiusKm={Number(form.radius_km)}
              nearbyCampaigns={nearby}
              onChange={({ latitude, longitude }) =>
                setForm((p) => ({ ...p, latitude, longitude }))
              }
              height={320}
            />
          </div>
          <div className="mt-2 flex items-center justify-between text-[11px] text-muted-foreground">
            <span data-testid="cg-coord-readout">
              {probeLat.toFixed(5)}, {probeLng.toFixed(5)} · radius {Number(form.radius_km)} km
            </span>
            <span data-testid="cg-nearby-count">
              {nearbyLoading
                ? "Scanning nearby…"
                : overlapCount > 0
                  ? `⚠ ${overlapCount} competing campaign${overlapCount === 1 ? "" : "s"} overlap your boundary`
                  : nearby.length > 0
                    ? `${nearby.length} nearby, no direct overlap`
                    : "No competing campaigns within 25 km"}
            </span>
          </div>
        </div>

        <div className="mt-4 grid gap-3 md:grid-cols-2">
          <div><Label>Campaign name</Label><Input value={form.campaign_name} onChange={(e) => setForm({ ...form, campaign_name: e.target.value })} placeholder="e.g. Monsoon knee-pain clinic" data-testid="cg-campaign-name" /></div>
          <div>
            <Label>Radius: {form.radius_km} km <span className="text-[10px] text-muted-foreground">({form.target_type.toUpperCase()} lock active)</span></Label>
            <input type="range" min={minR} max={maxR} step="0.1" value={form.radius_km}
                   onChange={(e) => setForm({ ...form, radius_km: e.target.value })}
                   className="w-full accent-primary mt-2" data-testid="cg-radius" />
          </div>
          <div><Label>Daily budget (₹)</Label><Input type="number" value={form.daily_budget_inr} onChange={(e) => setForm({ ...form, daily_budget_inr: e.target.value })} data-testid="cg-budget" /></div>
        </div>
        <div className="mt-4 flex items-center gap-2 rounded-md bg-primary/5 border border-primary/15 p-3 text-xs text-primary">
          <Shield size={14} /> <span><b>AI Expansion Guardrail active.</b> Meta Advantage+ and automatic radius extension are programmatically locked OFF so budget never spills outside your boundary.</span>
        </div>
        <div className="mt-4 flex justify-end">
          <Button onClick={submit} disabled={!form.campaign_name.trim()} data-testid="cg-campaign-submit">Deploy campaign</Button>
        </div>
      </div>

      {list.length > 0 && (
        <div className="rounded-lg border border-border bg-white p-5">
          <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-3">Active campaigns</div>
          <ul className="divide-y divide-border">
            {list.map((c) => (
              <li key={c.id} className="py-2.5 text-sm flex items-center justify-between gap-2" data-testid={`cg-campaign-row-${c.id}`}>
                <div className="text-primary">{c.campaign_name}</div>
                <div className="text-xs text-muted-foreground">
                  {c.target_type} · {c.radius_km}km · {c.latitude.toFixed(3)},{c.longitude.toFixed(3)}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

// ---------- AI Triage ------------------------------------------------------
function Triage() {
  const [list, setList] = useState([]);
  const [form, setForm] = useState({ patient_name: "", patient_phone: "", raw_symptoms_text: "", consent_given: false });
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/clinic-growth/triage"); setList(data.consultations); }
    catch (e) { toast.error(formatApiError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    setBusy(true); setResult(null);
    try {
      const { data } = await api.post("/clinic-growth/triage/process", form);
      setResult(data);
      toast.success("AI triage complete — queued for RMP review");
      setForm({ patient_name: "", patient_phone: "", raw_symptoms_text: "", consent_given: false });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6">
      <div className="rounded-lg border border-border bg-white p-5">
        <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Patient intake</div>
        <div className="mt-1 font-serif text-xl text-primary">Capture symptoms — AI builds the clinical summary</div>
        <div className="mt-4 grid gap-3 md:grid-cols-2">
          <div><Label>Patient name</Label><Input value={form.patient_name} onChange={(e) => setForm({ ...form, patient_name: e.target.value })} data-testid="cg-triage-name" /></div>
          <div><Label>Patient phone</Label><Input value={form.patient_phone} onChange={(e) => setForm({ ...form, patient_phone: e.target.value })} placeholder="+91…" data-testid="cg-triage-phone" /></div>
        </div>
        <div className="mt-3"><Label>Raw symptoms (anything the patient types or dictates)</Label>
          <Textarea rows={4} value={form.raw_symptoms_text} onChange={(e) => setForm({ ...form, raw_symptoms_text: e.target.value })} placeholder="Fever 3 days, dry cough, mild chest tightness…" data-testid="cg-triage-symptoms" />
        </div>
        <div className="mt-3 flex items-start gap-2">
          <Checkbox checked={form.consent_given} onCheckedChange={(v) => setForm({ ...form, consent_given: !!v })} data-testid="cg-triage-consent" />
          <Label className="text-xs text-muted-foreground">
            Patient has given informed consent for AI-assisted triage under the Telemedicine Practice Guidelines & DPDP Act, 2023.
          </Label>
        </div>
        <div className="mt-4 flex justify-end">
          <Button onClick={submit} disabled={busy || !form.consent_given || !form.raw_symptoms_text.trim() || !form.patient_phone.trim()} data-testid="cg-triage-submit">
            {busy ? "Analysing…" : "Run AI triage"}
          </Button>
        </div>
      </div>

      {result && (
        <div className="rounded-lg border border-primary/15 bg-primary/5 p-5" data-testid="cg-triage-result">
          <div className="flex items-center gap-2">
            <ShieldAlert size={14} className="text-primary" />
            <div className="text-[11px] uppercase tracking-widest text-primary">AI structured summary</div>
            <Badge variant="outline">{result.ai_structured_summary.urgency_level}</Badge>
          </div>
          <div className="mt-2 text-sm text-primary">{result.ai_structured_summary.chief_complaint}</div>
          {result.ai_structured_summary.red_flags.length > 0 && (
            <div className="mt-2 text-xs text-rose-800">Red flags: {result.ai_structured_summary.red_flags.join(" · ")}</div>
          )}
          <div className="mt-3 text-[11px] uppercase tracking-widest text-primary">Suggested differentials</div>
          <ul className="mt-1 space-y-1 text-sm">
            {result.ai_suggested_diagnoses.map((d, i) => (
              <li key={i} className="text-primary">
                <b>{d.condition}</b> · <span className="text-muted-foreground">{d.match}</span>
                <div className="text-xs text-muted-foreground">{d.rationale}</div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {list.length > 0 && (
        <div className="rounded-lg border border-border bg-white p-5">
          <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-3">Triage queue</div>
          <ul className="divide-y divide-border">
            {list.map((c) => (
              <li key={c.id} className="py-2.5 text-sm" data-testid={`cg-triage-row-${c.id}`}>
                <div className="flex items-center justify-between">
                  <span className="text-primary">{c.patient_name}</span>
                  <Badge variant="outline">{c.triage_status.replace(/_/g, " ")}</Badge>
                </div>
                <div className="text-xs text-muted-foreground">
                  {c.ai_structured_summary?.chief_complaint} · urgency {c.ai_structured_summary?.urgency_level}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

// ---------- RMP Sign-off ---------------------------------------------------
function Signoff() {
  const [queue, setQueue] = useState([]);
  const [picked, setPicked] = useState(null);
  const [lastResult, setLastResult] = useState(null);
  const [form, setForm] = useState({ approved_diagnosis: "", treatment_plan: "", dispatch_whatsapp: true });

  const load = useCallback(async () => {
    try { const { data } = await api.get("/clinic-growth/triage?status=pending_doctor_review");
      setQueue(data.consultations);
    } catch (e) { toast.error(formatApiError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const sign = async () => {
    if (!picked) return;
    try {
      const { data } = await api.post("/clinic-growth/doctor/signoff", {
        consultation_id: picked.id, ...form });
      toast.success(`Signed off · WhatsApp dispatch: ${data.dispatch_status}`);
      setLastResult(data);
      setPicked(null); setForm({ approved_diagnosis: "", treatment_plan: "", dispatch_whatsapp: true });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (queue.length === 0 && !lastResult) {
    return <div className="rounded-lg border border-border bg-white p-10 text-center text-sm text-muted-foreground">No consultations awaiting your sign-off.</div>;
  }

  return (
    <div className="space-y-4">
      {lastResult && (
        <div className="rounded-lg border border-primary/20 bg-primary/5 p-5" data-testid="cg-signoff-receipt">
          <div className="flex items-center gap-2">
            <FileText size={14} className="text-primary" />
            <div className="text-[11px] uppercase tracking-widest text-primary">Last sign-off</div>
            <Badge variant="outline" data-testid="cg-signoff-dispatch-status">{lastResult.dispatch_status}</Badge>
          </div>
          <div className="mt-2 text-sm text-primary">
            {lastResult.signoff?.approved_diagnosis}
            {lastResult.signoff?.pdf_url ? (
              <a
                href={lastResult.signoff.pdf_url}
                target="_blank" rel="noopener noreferrer"
                className="ml-3 inline-flex items-center gap-1 text-accent underline"
                data-testid="cg-signoff-pdf-link"
              >
                <Download size={12} /> View signed PDF
              </a>
            ) : lastResult.signoff?.pdf_error ? (
              <span className="ml-3 text-xs text-rose-700">PDF generation failed</span>
            ) : null}
          </div>
        </div>
      )}

      {queue.length === 0 ? (
        <div className="rounded-lg border border-border bg-white p-10 text-center text-sm text-muted-foreground">
          Queue is empty — new intakes will appear here as they come in.
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          <div className="rounded-lg border border-border bg-white p-5">
            <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-3">Awaiting sign-off ({queue.length})</div>
            <ul className="divide-y divide-border">
              {queue.map((c) => (
                <li key={c.id}>
                  <button onClick={() => { setPicked(c);
                      setForm({ approved_diagnosis: c.ai_suggested_diagnoses?.[0]?.condition || "",
                                treatment_plan: "", dispatch_whatsapp: true }); }}
                    className={`w-full text-left py-2.5 text-sm hover:bg-muted/30 px-2 rounded ${picked?.id === c.id ? "bg-primary/5" : ""}`}
                    data-testid={`cg-signoff-pick-${c.id}`}>
                    <div className="flex items-center justify-between">
                      <span className="text-primary">{c.patient_name}</span>
                      <Badge variant="outline">{c.ai_structured_summary?.urgency_level}</Badge>
                    </div>
                    <div className="text-xs text-muted-foreground">{c.ai_structured_summary?.chief_complaint}</div>
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {picked && (
            <div className="rounded-lg border border-primary/20 bg-primary/5 p-5" data-testid="cg-signoff-form">
              <div className="text-[11px] uppercase tracking-widest text-primary">Verify & dispatch</div>
              <div className="mt-1 font-serif text-xl text-primary">{picked.patient_name}</div>
              <div className="mt-3">
                <Label>Approved diagnosis</Label>
                <Input value={form.approved_diagnosis} onChange={(e) => setForm({ ...form, approved_diagnosis: e.target.value })} data-testid="cg-signoff-diagnosis" />
              </div>
              <div className="mt-3">
                <Label>Treatment plan</Label>
                <Textarea rows={5} value={form.treatment_plan} onChange={(e) => setForm({ ...form, treatment_plan: e.target.value })} placeholder="Drug class · duration · when to review…" data-testid="cg-signoff-plan" />
              </div>
              <div className="mt-3 flex items-center gap-2">
                <Checkbox checked={form.dispatch_whatsapp} onCheckedChange={(v) => setForm({ ...form, dispatch_whatsapp: !!v })} data-testid="cg-signoff-dispatch" />
                <Label className="text-xs">Dispatch e-prescription PDF via WhatsApp</Label>
              </div>
              <div className="mt-4 flex justify-end">
                <Button onClick={sign} disabled={!form.approved_diagnosis.trim() || !form.treatment_plan.trim()} data-testid="cg-signoff-submit">
                  Sign & dispatch
                </Button>
              </div>
              <div className="mt-3 text-[10px] text-muted-foreground">
                A signed PDF is generated and uploaded on sign-off. The patient receives a link via WhatsApp; both land in your audit trail.
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
